import { CurrentUnits } from "@/libs/units/current";
import { GenericUnits } from "@/libs/units/generic";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { PercentageUnits } from "@/libs/units/percentage";
import { PowerUnits } from "@/libs/units/power";
import { TemperatureUnits } from "@/libs/units/temperature";
import { VoltageUnits } from "@/libs/units/voltage";
import { EquipmentCalculatorSectionKey } from "../../calculationTypes";
import type { Section, SectionDeps } from "../types";
import { ScenarioDetailSectionKey, ScenarioDetailSectionTitle } from "../types";

export const buildCablesSideCalculatorSection = ({
  scenarioCalculations,
  getRealPowerUnit
}: SectionDeps): Section => ({
  id: EquipmentCalculatorSectionKey.CABLE,
  hash: ScenarioDetailSectionKey.CABLE,
  label: ScenarioDetailSectionTitle.CABLE,
  routeName: "SizingParameters",
  aggregateDisplays: [
    {
      label: "Total Cable Cost / Month",
      base: GenericUnits.UnitedStatesDollar,
      model: GenericUnits.options(),
      mode: "read",
      value:
        scenarioCalculations.cableCalculations.aggregate
          .totalCablesCostPerMonthUSD.value
    },
    {
      label: "Total Cables Voltage Loss",
      base: VoltageUnits.Volts,
      model: VoltageUnits.options(),
      mode: "convert",
      value:
        scenarioCalculations.cableCalculations.aggregate.totalCablesVoltLossV
          .value
    },
    {
      label: "Total Cables Power Loss",
      base: PowerUnits.KiloWatts,
      target: getRealPowerUnit().value,
      model: PowerUnits.options(),
      mode: "convert",
      value:
        scenarioCalculations.cableCalculations.aggregate.totalCablesPowerLossKW
          .value
    }
  ],
  individualDisplays: scenarioCalculations.orderedCables.value.map(
    xrefCable => {
      const calcs =
        scenarioCalculations.cableCalculations.individual.value[xrefCable.id];
      return {
        label: calcs.cableHeader,
        displays: [
          {
            label: "Cable Length ",
            base: LengthUnits.Feet,
            model: LargeLengthUnits.options(),
            mode: "convert",
            value: calcs.cableLengthFT
          },
          {
            label: "Average Ambient Temperature",
            base: TemperatureUnits.Fahrenheit,
            model: TemperatureUnits.options(),
            mode: "convert",
            value: calcs.averageAmbientTemperatureF
          },
          {
            label: "Cable Temperature",
            base: TemperatureUnits.Fahrenheit,
            model: TemperatureUnits.options(),
            mode: "convert",
            value: calcs.cableMaxTemperatureF
          },
          {
            label: "Cable Load",
            base: PercentageUnits.Ratio,
            target: PercentageUnits.Percentage,
            model: PercentageUnits.options(),
            mode: "read",
            value: calcs.cableMotorLoadRatio
          },
          {
            label: "Max Amps at Temp",
            base: CurrentUnits.Amperes,
            model: [CurrentUnits.Amperes],
            mode: "read",
            value: calcs.maxAmpsAtMaxTemperatureA
          },
          {
            label: "Volt Loss / 1000ft",
            base: VoltageUnits.Volts,
            model: VoltageUnits.options(),
            mode: "convert",
            value: calcs.voltLossPerThousandFTV
          },
          {
            label: "Total Volt Loss",
            base: VoltageUnits.Volts,
            model: VoltageUnits.options(),
            mode: "convert",
            value: calcs.totalVoltLossV
          },
          {
            label: "Power Loss",
            base: PowerUnits.KiloWatts,
            target: getRealPowerUnit().value,
            model: PowerUnits.options(),
            mode: "read",
            value: calcs.powerLossKW
          },
          {
            label: "Cable Pwr Cost/Mo",
            base: GenericUnits.UnitedStatesDollar,
            model: GenericUnits.options(),
            mode: "read",
            value: calcs.cablePowerCostPerMonthUSD
          }
        ]
      };
    }
  )
});
